import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, BarChart3, Play, Search, Flame, ChevronRight, Share2, RefreshCw, Users, X } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useFb } from "../../utils/football";
import { FootieBall } from "../../components/football/FootieIcon";
import { createSound, useNative, shareGame, STORE, MINICUP_PUBLIC } from "../../utils/minicup";
import PenaltyGame from "../../components/football/penalty/PenaltyGame";
import MiniCupTrophies from "../../components/football/penalty/MiniCupTrophies";
import MiniCupStandings from "../../components/football/penalty/MiniCupStandings";
import MiniCupErrorBoundary from "../../components/football/penalty/MiniCupErrorBoundary";
import { toast } from "sonner";

function InstallGate({ comingSoon }) {
  if (comingSoon) {
    return (
      <div className="football-theme mx-auto max-w-md px-4 py-10 text-center" data-testid="minicup-coming-soon">
        <div className="mx-auto mb-4 footie-ball-bounce"><FootieBall size={64} /></div>
        <h1 className="text-2xl font-extrabold">⚽ APL Penalty Cup</h1>
        <p className="mt-2 text-sm text-muted-foreground">Coming soon — get ready to beat the keeper!</p>
        <p className="mt-4 text-xs text-muted-foreground">We're putting the finishing touches on the World Cup mini-game. Check back shortly.</p>
      </div>
    );
  }
  return (
    <div className="football-theme mx-auto max-w-md px-4 py-10 text-center" data-testid="minicup-install-gate">
      <div className="mx-auto mb-4 footie-ball-bounce"><FootieBall size={64} /></div>
      <h1 className="text-2xl font-extrabold">📱 Get APL Penalty Cup</h1>
      <p className="mt-2 text-sm text-muted-foreground">Play the World Cup mini-game in the APL app</p>
      <div className="mt-6 flex justify-center gap-3">
        <a href={STORE.ios} target="_blank" rel="noreferrer" className="rounded-lg bg-slate-900 px-4 py-3 text-sm font-bold text-white" data-testid="gate-ios">App Store</a>
        <a href={STORE.android} target="_blank" rel="noreferrer" className="rounded-lg wc-bg-gold px-4 py-3 text-sm font-extrabold text-slate-900" data-testid="gate-android">Google Play</a>
      </div>
      <p className="mt-4 text-xs text-muted-foreground">Already have the app? Open it and find Penalty Cup in the Football section.</p>
    </div>
  );
}

function TeamPicker({ teams, current, onStart }) {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(current?.code || null);
  const filtered = useMemo(() => teams.filter((t) => t.name.toLowerCase().includes(q.toLowerCase())), [teams, q]);
  return (
    <div data-testid="minicup-team-picker">
      <h2 className="mb-3 text-lg font-extrabold">Choose Your Team</h2>
      <div className="relative mb-3">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search team name..."
          className="w-full rounded-lg border border-border bg-card py-2.5 pl-9 pr-3 text-sm" data-testid="team-search" />
      </div>
      <div className="max-h-[46vh] space-y-1.5 overflow-y-auto pb-2">
        {filtered.map((t) => (
          <button key={t.code} onClick={() => setSel(t.code)}
            className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left ${sel === t.code ? "border-amber-400 bg-amber-400/15" : "border-border bg-card"}`}
            data-testid={`team-option-${t.code}`}>
            {t.flag_url ? <img src={t.flag_url} alt="" className="h-5 w-7 rounded object-cover" /> : <span className="h-5 w-7" />}
            <span className="flex-1 text-sm font-bold">{t.name}</span>
            <span className="text-xs text-muted-foreground">{t.code}</span>
          </button>
        ))}
      </div>
      <div className="sticky bottom-20 z-20 mt-3 -mx-1 rounded-xl bg-background/80 p-1 backdrop-blur">
        <button disabled={!sel} onClick={() => onStart(sel)}
          className="w-full rounded-xl wc-bg-gold py-3 text-base font-extrabold text-slate-900 disabled:opacity-50" data-testid="picker-start-btn">
          Start Match
        </button>
      </div>
    </div>
  );
}

function QuickStart({ team, stats, onStart, onChange }) {
  return (
    <div data-testid="minicup-quickstart">
      <div className="wc-hero-gradient relative overflow-hidden rounded-2xl p-5 text-center">
        <div className="mx-auto mb-2 footie-ball-bounce"><FootieBall size={48} /></div>
        <h1 className="text-xl font-extrabold text-white">APL Penalty Cup</h1>
        <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-black/30 px-3 py-1.5 text-sm font-bold text-white">
          {team?.flag_url && <img src={team.flag_url} alt="" className="h-4 w-6 rounded object-cover" />}
          {team?.name}
        </div>
        <div><button onClick={onChange} className="mt-2 text-xs text-amber-200 underline" data-testid="change-team-link">Change Team</button></div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        <div className="rounded-xl border border-border bg-card p-3 text-center"><div className="text-2xl font-black wc-gold">{stats?.total_goals ?? 0}</div><div className="text-[10px] text-muted-foreground">Total Goals</div></div>
        <div className="rounded-xl border border-border bg-card p-3 text-center"><div className="text-2xl font-black wc-gold">{stats?.best_match ?? 0}</div><div className="text-[10px] text-muted-foreground">Best Match</div></div>
        <div className="rounded-xl border border-border bg-card p-3 text-center"><div className="flex items-center justify-center gap-1 text-2xl font-black wc-gold">{stats?.streak ?? 0}{(stats?.streak ?? 0) > 0 && <Flame size={18} className="text-orange-400" />}</div><div className="text-[10px] text-muted-foreground">Streak</div></div>
      </div>

      <button onClick={() => onStart(team.code)} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl wc-bg-gold py-4 text-lg font-black text-slate-900" data-testid="start-match-btn">
        <Play size={20} /> START MATCH
      </button>
    </div>
  );
}

function Summary({ result, team, stats, sponsor, onAgain, onChange, onClose }) {
  const share = async () => {
    const msg = `Just scored ${result.goals} goals for ${team?.name} in APL Penalty Cup ⚽ Beat me at https://apnapremierleague.com/football/mini-cup`;
    const r = await shareGame(msg);
    if (r === "copied") toast.success("Copied! Share it anywhere.");
  };
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="minicup-summary"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="football-theme relative w-full max-w-sm rounded-2xl border border-border bg-card p-6 text-center">
        <button onClick={onClose} className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full bg-muted text-muted-foreground" data-testid="summary-close-btn" aria-label="close"><X size={18} /></button>
        <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Match Over</h2>
        <div className="my-2 text-6xl font-black wc-gold" data-testid="summary-goals">{result.goals}</div>
        <p className="text-xs text-muted-foreground">goals scored</p>
        <div className="mt-3 flex justify-around text-sm">
          <div><div className="font-bold">{stats?.best_match ?? 0}</div><div className="text-[10px] text-muted-foreground">Best</div></div>
          <div><div className="font-bold">{stats?.streak ?? 0}</div><div className="text-[10px] text-muted-foreground">Streak</div></div>
          <div><div className="font-bold">{stats?.total_goals ?? 0}</div><div className="text-[10px] text-muted-foreground">Career</div></div>
        </div>
        {result.new_trophies?.length > 0 && (
          <div className="mt-3 space-y-1" data-testid="summary-new-trophies">
            {result.new_trophies.map((t) => (
              <div key={t.trophy_id} className="rounded-lg bg-amber-400/15 px-3 py-1.5 text-sm font-bold text-amber-200">🆕 New Trophy: {t.title} {["Bronze","Silver","Gold","Diamond"][t.tier-1]}</div>
            ))}
          </div>
        )}
        <div className="mt-5 space-y-2">
          <button onClick={onAgain} className="flex w-full items-center justify-center gap-2 rounded-xl wc-bg-gold py-3 font-extrabold text-slate-900" data-testid="play-again-btn"><RefreshCw size={16} /> Play Again</button>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={onChange} className="flex items-center justify-center gap-1.5 rounded-xl border border-border bg-card py-2.5 text-sm font-bold" data-testid="summary-change-team-btn"><Users size={15} /> Change Team</button>
            <button onClick={share} className="flex items-center justify-center gap-1.5 rounded-xl border border-border bg-card py-2.5 text-sm font-bold" data-testid="share-win-btn"><Share2 size={15} /> Share Win</button>
          </div>
        </div>
        <p className="mt-4 text-[11px] font-semibold text-muted-foreground" data-testid="summary-footer">
          {sponsor ? `APL Penalty Cup · Powered by ${sponsor.sponsor_name}` : "APL Penalty Cup"}
        </p>
      </div>
    </div>
  );
}

export default function MiniCupPage() {
  const fb = useFb();
  const navigate = useNavigate();
  const { dbUser } = useAuth();
  const native = useNative();
  const sound = useRef(createSound());

  const [tab, setTab] = useState("play");
  const [teams, setTeams] = useState([]);
  const [me, setMe] = useState(null);
  const [sponsor, setSponsor] = useState(null);
  const [picking, setPicking] = useState(false);
  const [screen, setScreen] = useState("home"); // home | game | summary
  const [activeTeam, setActiveTeam] = useState(null);
  const [matchId, setMatchId] = useState(null);
  const [result, setResult] = useState(null);

  const isAdmin = !!dbUser?.is_admin;
  // Pre-launch: admins always play; everyone else is gated regardless of platform.
  // After launch (MINICUP_PUBLIC=true): only web non-admins are gated (native plays).
  const gated = MINICUP_PUBLIC ? (!native && !isAdmin && dbUser != null) : (!isAdmin && dbUser != null);
  const comingSoon = !MINICUP_PUBLIC && !isAdmin;

  // audio leak fix: kill all game audio if the page unmounts (navigate away)
  useEffect(() => {
    const s = sound.current;
    return () => { try { s.stopAll(); } catch {} };
  }, []);

  const logSponsorClick = async (surface) => {
    if (!sponsor) return;
    try { await fb.post("/football/mini-cup/sponsor/click", { surface }); } catch {}
  };

  const loadMe = async () => {
    try { const d = await fb.get("/football/mini-cup/me"); setMe(d); return d; } catch { return null; }
  };

  useEffect(() => {
    if (gated) return;
    (async () => {
      try { const d = await fb.get("/football/mini-cup/teams"); setTeams(d.teams || []); } catch {}
      try { const s = await fb.get("/football/mini-cup/sponsor"); setSponsor(s.sponsor || null); } catch {}
      const d = await loadMe();
      if (d && !d.current_team) setPicking(true);
      sound.current.preload();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gated]);

  const startingRef = useRef(false);
  const startMatch = async (code) => {
    if (startingRef.current) return;
    startingRef.current = true;
    try {
      if (!me?.current_team || me.current_team.code !== code) {
        await fb.post("/football/mini-cup/team", { team_code: code });
      }
      const d = await fb.post("/football/mini-cup/match/start", { team_code: code });
      setActiveTeam(d.team); setMatchId(d.match_id); setPicking(false); setScreen("game");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start match"); }
    finally { setTimeout(() => { startingRef.current = false; }, 600); }
  };

  const onGameEnd = async (goals, durationMs, shotLog) => {
    try {
      const d = await fb.post("/football/mini-cup/match/complete", { match_id: matchId, goals_scored: goals, duration_ms: durationMs, shot_log: shotLog });
      setResult({ goals, new_trophies: d.new_trophies || [] });
    } catch (e) {
      setResult({ goals, new_trophies: [] });
    }
    await loadMe();
    setScreen("summary");
  };

  if (gated) return <InstallGate comingSoon={comingSoon} />;
  if (!dbUser) return <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>;

  if (screen === "game" && activeTeam) {
    return <PenaltyGame team={activeTeam} sponsor={sponsor} sound={sound.current}
      onEnd={onGameEnd} onSponsorClick={logSponsorClick} key={matchId} />;
  }

  return (
    <div className="football-theme pb-10" data-testid="minicup-page">
      {/* Tabs */}
      <div className="mb-4 grid grid-cols-3 gap-2">
        {[{ k: "trophies", label: "Trophies", icon: Trophy }, { k: "play", label: "Play", icon: Play }, { k: "standings", label: "Standings", icon: BarChart3 }].map(({ k, label, icon: Icon }) => (
          <button key={k} onClick={() => setTab(k)}
            className={`flex items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm font-bold ${tab === k ? "wc-bg-gold text-slate-900" : "border border-border bg-card text-muted-foreground"}`}
            data-testid={`minicup-tab-${k}`}>
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>

      <MiniCupErrorBoundary>
        {tab === "play" && (
          picking || !me?.current_team
            ? <TeamPicker teams={teams} current={me?.current_team} onStart={startMatch} />
            : <QuickStart team={me.current_team} stats={me.user_stats} onStart={startMatch} onChange={() => setPicking(true)} />
        )}
        {tab === "trophies" && <MiniCupTrophies stats={me?.user_stats} unlocked={me?.unlocked_trophies} />}
        {tab === "standings" && <MiniCupStandings />}
      </MiniCupErrorBoundary>

      {screen === "summary" && result && (
        <Summary result={result} team={activeTeam} stats={me?.user_stats} sponsor={sponsor}
          onAgain={() => { setResult(null); startMatch(activeTeam.code); }}
          onChange={() => { setResult(null); setScreen("home"); setTab("play"); setPicking(true); }}
          onClose={() => { setResult(null); setScreen("home"); setTab("play"); }} />
      )}
    </div>
  );
}
